import React, { useRef, useState } from 'react';
import '../../styles/modal.css';
import '../../styles/file-import.css';

const ExcelImportModal = ({ open, onClose, onFileSelected, uploading = false, error = null, result = null, onClearResult = () => {} }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const onBrowseClick = () => inputRef.current?.click();

  const onInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) onFileSelected(file);
  };

  const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };

  const onDragEnter = (e) => { prevent(e); setDragActive(true); };
  const onDragOver = (e) => { prevent(e); setDragActive(true); };
  const onDragLeave = (e) => { prevent(e); setDragActive(false); };
  const onDrop = (e) => {
    prevent(e);
    setDragActive(false);
    const file = e.dataTransfer?.files && e.dataTransfer.files[0];
    if (file) onFileSelected(file);
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Import Excel</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="import-error">{String(error)}</div>
          )}
          {!result && (
            <div
              className={`dropzone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden-input"
                onChange={onInputChange}
              />
              <div className="dropzone-content">
                <div className="dz-icon" aria-hidden="true">📄</div>
                <div className="dz-title">Drag & drop your Excel file</div>
                <div className="dz-subtitle">or</div>
                <button className="dz-browse" type="button" onClick={onBrowseClick} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Browse files'}
                </button>
                <div className="dz-hint">Supported: .xlsx, .xls, .csv</div>
              </div>
            </div>
          )}

          {result && (
            <div className="import-results" style={{ marginTop: '12px' }}>
              <div className="results-header" style={{ fontWeight: 600, marginBottom: '6px' }}>
                Import Results
              </div>
              <div className="results-summary" style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
                <div>Success: {String(Boolean(result.success))}</div>
                {result.message && <div>Message: {result.message}</div>}
                {result.summary && (
                  <div className="stack-on-phone" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, auto)', gap: '12px', marginTop: 6 }}>
                    <div>Total Sheets: <strong>{result.summary.totalSheets ?? '-'}</strong></div>
                    <div>Total Records: <strong>{result.summary.totalRecords ?? '-'}</strong></div>
                    <div>Imported: <strong style={{ color: '#065f46' }}>{result.summary.imported ?? '-'}</strong></div>
                    <div>Skipped: <strong style={{ color: '#92400e' }}>{result.summary.skipped ?? '-'}</strong></div>
                    <div>Failed: <strong style={{ color: '#991b1b' }}>{result.summary.failed ?? '-'}</strong></div>
                  </div>
                )}
              </div>
              {result.details && typeof result.details === 'object' && (
                <div className="results-details" style={{ borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
                  {Object.entries(result.details).map(([entity, info]) => (
                    <div key={entity} style={{ marginBottom: 10 }}>
                      <div style={{ fontWeight: 600 }}>{entity}</div>
                      <div style={{ fontSize: 14, color: '#374151' }}>
                        <span style={{ marginRight: 12 }}>Imported: <strong style={{ color: '#065f46' }}>{info?.imported ?? '-'}</strong></span>
                        <span style={{ marginRight: 12 }}>Skipped: <strong style={{ color: '#92400e' }}>{info?.skipped ?? '-'}</strong></span>
                        <span>Failed: <strong style={{ color: '#991b1b' }}>{info?.failed ?? '-'}</strong></span>
                      </div>
                      {Array.isArray(info?.errors) && info.errors.length > 0 && (
                        <ul style={{ marginTop: 6, paddingLeft: 18, color: '#991b1b' }}>
                          {info.errors.map((err, idx) => (
                            <li key={idx} style={{ fontSize: 13 }}>{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {!result ? (
            <button className="secondary-btn" onClick={onClose} disabled={uploading}>Cancel</button>
          ) : (
            <>
              <button className="secondary-btn" onClick={() => { onClearResult(); }} disabled={uploading}>Clear</button>
              <button className="primary-btn" onClick={onClose} style={{ marginLeft: 8 }}>Close</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImportModal;
